import * as React from 'react';
import './styles.scss';
import { FC, useEffect, useState, useCallback } from 'react';
import { AppointmentEntity, ClientEntity, ClientPackEntity, Entity } from "../../store/entities/state";
import { TK } from "../../store/translations/translationKeys";
import { Translate, getLanguage, useTranslations } from '../../store/translations/hooks';
import Typography from '../../components/Typography';
import { AppointmentData, ChangeInvoiceState, ClientData, CompanyLegalInfo, CreditNoteData, FeatureEnum, Invoice, InvoiceManyAppointmentsData, InvoiceData, InvoiceDocumentType, InvoiceItem, InvoiceItemDetails, InvoiceItemType, InvoiceReduced, InvoiceState, InvoicesProvider, MarkInvoicePaidData, Payment, PaymentStatus, PermissionEnum } from '../../models';
import { createInvoiceForAppointmentAsync, createInvoiceForAppointmentsAsync, createInvoiceForPackAsync, createStandaloneInvoiceAsync, generateInvoicePdfAsync, getInvoiceAsync, getInvoicesAsync, sendInvoiceByEmailAsync, updateInvoiceStateAsync, getClientAsync, issueCreditNoteAsync, emitInvoiceReceiptAsync, getClientPackAsync, getAppointmentAsync } from '../../ajax/requests';
import { getErrorMessage } from '../../ajax/errors';
import { ListGroup, ListGroupItem, ModalBody, ModalFooter, ModalHeader, Modal } from 'reactstrap';
import { formatEuros } from '../../shared/string';
import moment from 'moment';
import DownloadIcon from '@material-ui/icons/GetAppRounded'
import MailIcon from '@material-ui/icons/EmailRounded'
import CheckIcon from '@material-ui/icons/CheckCircleRounded'
import CloseIcon from '@material-ui/icons/CancelRounded'
import DeleteIcon from '@material-ui/icons/DeleteRounded'
import ReceiptIcon from '@material-ui/icons/ReceiptRounded'
import CreditCardIcon from '@material-ui/icons/CreditCardRounded'
import EventIcon from '@material-ui/icons/EventRounded'
import LocalOfferIcon from '@material-ui/icons/LocalOfferRounded'
import { Checkbox, IconButton } from '@material-ui/core'
import ToolTip from '../../components/ToolTip';
import { useDispatch, useSelector } from 'react-redux';
import { alertError, alertGenericError, alertSuccess } from '../../store/alerts/actions';
import { openEntity, entitiesLoaded } from '../../store/entities/actions';
import { AutocompleteTextField, CheckInputField, FormField, NumberInputField, TextInputField } from '../../components/Forms/fields';
import { getGeneralSettings, getExtrasSettings, getInvoiceSettings, getLatestPackTemplates, getSettings } from '../../store/settings/selectors';
import { useGeneralSettings } from '../../store/settings/hooks';
import { useTaxData } from '../../hooks/useTaxData';
import { getEntity } from '../../store/entities/selectors';
import { InvoiceForm } from '../../components/Forms/InvoiceForm';
import { ApplicationState } from '../../store';
import { hide } from '../../shared/style';
import { toDateOnlyString } from '../../shared/utils';
import {
    calculateAppointmentTotal,
    calculateAppointmentTotalWithVAT,
    calculateClientPackTotal,
    calculateClientPackTotalWithVAT,
    calculateInvoiceItemsTotalWithVAT,
    calculateInvoiceTotal,
    calculateInvoiceDiscount,
    isCompletedInvoicePayment,
} from '../../shared/invoiceCalculations';
import { applyAppointmentPlaceholders, buildAppointmentNotes, DEFAULT_APPOINTMENT_PACK_DETAILS_TEMPLATE, mapExtrasToInvoiceItems, normalizeTaxString, resolveTaxDetails, resolveEffectiveLegalInfo } from '../../shared/appointmint/invoice';
import { getPermissions, hasFeature } from '../../store/session/selectors';
import { getSelectedBranch, getSelectedCashRegisterName } from '../../store/general/selectors';
import { Actions as GeneralActions } from '../../store/general';
import AppButton from '../../components/AppButton';

const getInvoiceTypeText = (invoice: Pick<InvoiceData, 'type'>, t: Translate<TK>): string => {
    switch(invoice.type) {
        case InvoiceDocumentType.Invoice: return t(TK.invoice)
        case InvoiceDocumentType.InvoiceReceipt: return t(TK.invoiceReceipt)
        case InvoiceDocumentType.SimplifiedInvoice: return t(TK.simplifiedInvoice)
        case InvoiceDocumentType.CreditNote: return t(TK.creditNote)
        case InvoiceDocumentType.Receipt: return t(TK.receipt)
        default: return InvoiceDocumentType[invoice.type]
    }
}

const buildPackObservation = (pack: ClientPackEntity, t: Translate<TK>): string => {
    const dateLabel = pack.creationDate
        ? moment(pack.creationDate).format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD');
    const typeLabel = pack.isSubscription ? t(TK.subscription) : t(TK.pack);
    const packLabel = pack.packName || typeLabel;
    return `${dateLabel} - ${typeLabel} - ${packLabel} - ${formatEuros(pack.price)}`;
}

const toInvoiceReduced = (invoice: Invoice): InvoiceReduced & Pick<Invoice, 'sentByEmail' | 'sentByEmailAt' | 'relatedDocuments'> => ({
    id: invoice.id,
    number: invoice.number,
    type: invoice.type,
    state: invoice.state,
    total: invoice.total,
    date: invoice.date,
    dueDate: invoice.dueDate,
    clientId: invoice.clientId,
    clientName: invoice.clientName,
    address: invoice.address,
    city: invoice.city,
    country: invoice.country,
    clientTaxNumber: invoice.clientTaxNumber,
    reference: invoice.reference,
    observations: invoice.observations,
    currencyCode: invoice.currencyCode,
    branchId: invoice.branchId,
    appointmentId: invoice.appointmentId,
    packId: invoice.packId,
    sentByEmail: invoice.sentByEmail,
    sentByEmailAt: invoice.sentByEmailAt,
    relatedDocuments: invoice.relatedDocuments,
});

const ensureInvoiceReduced = (invoice: Invoice | InvoiceReduced): InvoiceReduced => (
    'items' in invoice ? toInvoiceReduced(invoice as Invoice) : invoice
)

type ServiceTaxSettings = {
    invoiceItemDetails?: Pick<InvoiceItemDetails, 'taxId'>
}

type PackTaxSettings = {
    id: string
    templateId?: string
    version: number
    invoiceItemDetails?: Pick<InvoiceItemDetails, 'taxId'>
}

export const resolveSourceTaxId = (
    appointmentType: string | undefined,
    clientPack: Pick<ClientPackEntity, 'packTemplateId' | 'packVersion'> | undefined,
    services: Record<string, ServiceTaxSettings> | undefined,
    packsSettings: PackTaxSettings[],
    companyTaxId?: string,
): string | undefined => {
    if (appointmentType) {
        return services?.[appointmentType]?.invoiceItemDetails?.taxId ?? companyTaxId
    }

    if (clientPack) {
        return packsSettings.find(
            p => (p.templateId ?? p.id) === clientPack.packTemplateId && p.version === clientPack.packVersion
        )?.invoiceItemDetails?.taxId ?? companyTaxId
    }

    return undefined
}

export { isCompletedInvoicePayment } from '../../shared/invoiceCalculations';

export const buildManualInvoicePayment = (
    paymentMethod: string,
    value: number,
    date: string = moment().format('YYYY-MM-DDTHH:mm:ss'),
    cashRegisterName?: string,
): Payment => ({
    status: PaymentStatus.Manual,
    date,
    type: paymentMethod,
    value,
    ...(cashRegisterName ? { cashRegisterName } : {}),
})

export const resolveInitialInvoiceFormPaymentMethod = (
    payments: Payment[] | undefined,
    defaultPaymentMethod?: string | null,
): string => (payments?.length ?? 0) > 0 ? '' : (defaultPaymentMethod ?? '')

export const buildAutomaticInvoiceReceiptPayment = ({
    invoiceTotal,
    paymentMethod,
    isFromAppointmentOrPack,
    modalPayments,
    sourcePayments,
    date,
    cashRegisterName,
}: {
    invoiceTotal: number
    paymentMethod?: string
    isFromAppointmentOrPack: boolean
    modalPayments?: Payment[]
    sourcePayments?: Payment[]
    date?: string
    cashRegisterName?: string
}): Payment | null => {
    if (!paymentMethod) {
        return null
    }

    if (isFromAppointmentOrPack) {
        const payments = sourcePayments ?? []
        const hasCompletedPayments = payments.some(isCompletedInvoicePayment)
        const hasInProgressPayments = payments.some(p => p.status === PaymentStatus.InProgress)
        if (hasCompletedPayments || hasInProgressPayments) {
            return null
        }

        return buildManualInvoicePayment(paymentMethod, invoiceTotal, date, cashRegisterName)
    }

    if ((modalPayments?.length ?? 0) > 0) {
        return null
    }

    return buildManualInvoicePayment(paymentMethod, invoiceTotal, date, cashRegisterName)
}

export type SyncDeps = {
    dispatch: (action: any) => void
    getAppointment: (id: string) => Promise<Entity>
    getClientPack: (clientId: string, packId: string) => Promise<Entity>
}

export const resolveInvoiceTableClient = (
    clientId: string,
    clientProp?: ClientEntity,
    clientFromRedux?: ClientEntity,
    fetchedClient?: ClientEntity | null,
): ClientEntity | undefined => {
    const matchingFetchedClient = fetchedClient?.id === clientId ? fetchedClient : undefined
    return clientProp || matchingFetchedClient || clientFromRedux
}

export const shouldFetchInvoiceTableClient = (
    clientId: string,
    clientProp: ClientEntity | undefined,
    clientFromRedux: ClientEntity | undefined,
    fetchedClient: ClientEntity | null,
    isLoadingClient: boolean,
): boolean => {
    if (!clientId || isLoadingClient) {
        return false
    }

    const matchingFetchedClient = fetchedClient?.id === clientId ? fetchedClient : null
    const hasCompleteClientData = !!clientProp || matchingFetchedClient !== null
    const clientExistsButIncomplete = !!clientFromRedux
        && !clientFromRedux.subsystemName
        && !clientFromRedux.subsystemNumber
    const clientMissing = !clientProp && !clientFromRedux && !matchingFetchedClient

    return (clientMissing || clientExistsButIncomplete) && !hasCompleteClientData
}

export const syncSourceEntityAfterEmission = (
    appointment: { id: string } | undefined,
    clientPack: { id: string; clientId?: string } | undefined,
    clientId: string | undefined,
    deps: SyncDeps,
) => {
    if (appointment) {
        deps.getAppointment(appointment.id)
            .then((updated) => deps.dispatch(entitiesLoaded('appointments', [updated], [], 'AppendResult')))
            .catch((error) => {
                console.error('Error syncing appointment after invoice emission:', error)
            })
        return
    }

    if (clientPack) {
        const packClientId = clientPack.clientId || clientId
        if (!packClientId) return

        deps.getClientPack(packClientId, clientPack.id)
            .then((updated) => deps.dispatch(entitiesLoaded('clientPacks', [updated], [], 'AppendResult')))
            .catch((error) => {
                console.error('Error syncing pack after invoice emission:', error)
            })
    }
}

const invoiceStateText = (t: Translate<TK>, state: InvoiceState): string => {
    switch(state) {
        case InvoiceState.Draft: return `(${t(TK.draft)})`
        case InvoiceState.Issued: return `(${t(TK.issued)})`
        case InvoiceState.Canceled: return `(${t(TK.canceled)})`
        default: return ''
    }
}

const invoiceStateColor = (state: InvoiceState, type?: InvoiceDocumentType): string => {
    if (type === InvoiceDocumentType.CreditNote) return 'red'

    switch(state) {
        case InvoiceState.None:
        case InvoiceState.Draft: return '#666'
        case InvoiceState.Issued: return 'green'
        case InvoiceState.Canceled: return 'red'
    }
}

const getInvoiceContentDisplay = (invoice: Invoice | InvoiceReduced): React.ReactElement | null => {
    // If it's a full Invoice with items, show items summary
    if ('items' in invoice && invoice.items?.length > 0) {
        return (
            <>
                {invoice.items.map((item, idx) => (
                    <div key={idx}>
                        {item.quantity}x {item.code}
                    </div>
                ))}
            </>
        )
    }

    // Otherwise show invoice number (InvoiceReduced behavior)
    return invoice.number ? <Typography size='small'>{invoice.number}</Typography> : null
}

const getBranchDisplayName = (
    branchId: string | undefined,
    branches: Record<string, { displayName: string }> | undefined,
    t: Translate<TK>,
) => {
    if (!branchId) {
        return undefined
    }

    return branches?.[branchId]?.displayName ?? branchId ?? t(TK.noBranch)
}

const invoiceDescription = (
    invoice: InvoiceReduced,
    t: Translate<TK>,
    onClick: () => void,
    branchLabel?: string,
    showClientName?: boolean,
): React.ReactElement => {
    const invoiceDate = toDateOnlyString(invoice.date) ?? '-';
    return (
        <div
            className='to-flex-column-on-sm'
            onClick={onClick}
            style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacer-S)', cursor: 'pointer'}}
        >
            <div style={{minWidth: '7.5rem', display: 'flex', flexDirection: 'column'}}>
                <Typography>{getInvoiceTypeText(invoice, t)}</Typography>
                <b>{invoiceDate}</b>
                {showClientName && invoice.clientName && (
                    <Typography size='small' style={{color: '#666'}}>{invoice.clientName}</Typography>
                )}
                {branchLabel && (
                    <Typography size='small' style={{color: '#666'}}>{t(TK.branch)}: {branchLabel}</Typography>
                )}
            </div>
            <div style={{display: 'flex', flexDirection: 'column'}}>
                {getInvoiceContentDisplay(invoice)}
            </div>
            <div style={{minWidth: '5.5rem', display: 'flex', flexDirection: 'column', alignItems: 'end', color: invoiceStateColor(invoice.state, invoice.type)}}>
                <Typography size='large'><b>{formatEuros(invoice.total)}</b></Typography>
                <Typography size='smaller'>{invoiceStateText(t, invoice.state)}</Typography>
            </div>
        </div>
    )
}


export interface ClientInvoicesTableProps {
    isOpen?: boolean
    client: ClientEntity
}

const ClientInvoicesTable : FC<ClientInvoicesTableProps> = ({isOpen, client}) => {
    const t = useTranslations()
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const [invoices, setInvoices] = useState<InvoiceReduced[]>([])
    const useBranches = useSelector(hasFeature(FeatureEnum.Branches))
    const selectedBranch = useSelector(getSelectedBranch)
    const activeBranchId = useBranches ? selectedBranch : undefined

    useEffect(() => {
        if (!isOpen) return

        setLoading(true)
        getInvoicesAsync(client.id, activeBranchId)
            .then(setInvoices)
            .catch((error) => {
                const message = getErrorMessage(error)
                if (message) dispatch(alertError(message))
                else dispatch(alertGenericError())
            })
            .finally(() => setLoading(false))
    }, [activeBranchId, client.id, dispatch, isOpen])

    return (
        <InvoicesTable
            clientId={client.id}
            client={client}
            invoices={invoices}
            loading={loading}
            createButtonStyle='button'
            onCreated={invoice => setInvoices(prev => [invoice, ...prev])}
            onUpdate={invoice => setInvoices(prev => prev.map(i => i.id === invoice.id ? invoice : i))}
            showNavigationButtons={true}
            createButtonLabel={t(TK.emitStandaloneInvoice)}
        />
    )
}

const isInvoiceVisualization = (invoice: Invoice|InvoiceData|null): invoice is Invoice => !!invoice && 'id' in invoice
const isInvoiceCreation = (invoice: Invoice|InvoiceData|null): invoice is InvoiceData => !!invoice && !('id' in invoice)


export const InvoicesTable: FC<{
    clientId: string,
    client?: ClientEntity,
    invoices: (Invoice | InvoiceReduced)[],
    appointment?: AppointmentEntity
    clientPack?: ClientPackEntity
    loading?: boolean,
    createNew?: boolean,
    emitReceiptForInvoiceId?: string,
    showClientName?: boolean,
    hideCreateButton?: boolean,
    showNavigationButtons?: boolean,
    compactActionButtons?: boolean,
    hideEmailButton?: boolean,
    allowCreditNoteCreation?: boolean,
    highlightPendingReceipt?: boolean,
    createButtonStyle?: 'icon' | 'button',
    createButtonLabel?: string,
    createPaymentMethod?: string,
    onUpdate: (invoice: InvoiceReduced) => void,
    onCreated?: (invoice: InvoiceReduced) => void,
    onReceiptEmitted?: () => void,
    onModalClose?: () => void,
}> = ({clientId, client: clientProp, invoices, loading, createNew, emitReceiptForInvoiceId, appointment, clientPack, showClientName, hideCreateButton, showNavigationButtons, compactActionButtons, hideEmailButton, allowCreditNoteCreation, highlightPendingReceipt, createButtonStyle = 'icon', createButtonLabel, createPaymentMethod, onCreated, onUpdate, onReceiptEmitted, onModalClose}) => {
    const t = useTranslations()
    const dispatch = useDispatch()
    const userPermissions = useSelector(getPermissions)
    const useBranches = useSelector(hasFeature(FeatureEnum.Branches))
    const selectedBranch = useSelector(getSelectedBranch)
    const selectedCashRegisterName = useSelector(getSelectedCashRegisterName)
    const settings = useSelector(getSettings)
    const generalSettings = useSelector(getGeneralSettings)
    const extrasSettings = useSelector(getExtrasSettings)
    const invoiceSettings = useSelector(getInvoiceSettings)
    const packsSettings = useSelector(getLatestPackTemplates) ?? []
    const clientFromRedux = useSelector((state: ApplicationState) => getEntity<ClientEntity>(state, 'clients', clientId))
    const [fetchedClient, setFetchedClient] = useState<ClientEntity | null>(null)
    const [isLoadingClient, setIsLoadingClient] = useState(false)
    const client = resolveInvoiceTableClient(clientId, clientProp, clientFromRedux, fetchedClient)
    const [fetching, setFetching] = useState(false)
    const [isLoadingInvoice, setIsLoadingInvoice] = useState(false)
    const [modalInvoice, setModalInvoice] = useState<Invoice|InvoiceData|null>(null)
    const [invoiceNavigationHistory, setInvoiceNavigationHistory] = useState<Invoice[]>([])
    const [confirming, setConfirming] = useState<'cancel'|'finalize'|'delete'|'resend'|'receipt'|null>(null)
    const [confirmingInvoice, setConfirmingInvoice] = useState<Invoice | InvoiceReduced | null>(null)
    const [cancelReason, setCancelReason] = useState('')
    const [receiptPaymentMethod, setReceiptPaymentMethod] = useState<string>('')
    const [receiptPaymentDate, setReceiptPaymentDate] = useState<string>(moment().format('YYYY-MM-DD'))
    const [receiptAmount, setReceiptAmount] = useState<number>(0)
    const [sendReceiptByEmail, setSendReceiptByEmail] = useState(false)
    const [associatedDocumentId, setAssociatedDocumentId] = useState<string>('')
    const [registerModalOpen, setRegisterModalOpen] = useState(false)
    const [pendingReceiptAction, setPendingReceiptAction] = useState<(() => void) | null>(null)

    const sharedRegisterNames = settings.cashRegisterSettings?.enabled
        ? (settings.cashRegisterSettings?.sharedRegisterNames ?? [])
        : []
    const needsRegisterSelection = sharedRegisterNames.length > 0 && selectedCashRegisterName === undefined

    const withRegisterCheck = (action: () => void) => {
        if (needsRegisterSelection) {
            setPendingReceiptAction(() => action)
            setRegisterModalOpen(true)
        } else {
            action()
        }
    }

    const handleRegisterSelect = (name: string) => {
        dispatch(GeneralActions.selectCashRegisterName(name))
        setRegisterModalOpen(false)
        pendingReceiptAction?.()
        setPendingReceiptAction(null)
    }
    const [isFinalConsumer, setIsFinalConsumer] = useState(false)
    const [selectedAppointmentIds, setSelectedAppointmentIds] = useState<string[]>([])
    const [showPaymentValidationModal, setShowPaymentValidationModal] = useState(false)
    const [paymentValidationData, setPaymentValidationData] = useState<{
        invoiceTotal: number,
        originalTotal: number,
        difference: number,
        payments: Payment[]
    } | null>(null)
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('')
    const [originalPaymentsFromSource, setOriginalPaymentsFromSource] = useState<Payment[]>([])
    const [invoiceFormPaymentMethod, setInvoiceFormPaymentMethod] = useState<string>('')
    const taxHook = useTaxData()
    const { paymentMethods } = useGeneralSettings()
    const receiptsSupported = invoiceSettings?.provider === InvoicesProvider.CegidVendus
    const sendByEmailLabel = getLanguage() === 'pt' ? 'Enviar por email' : 'Send by email'

    if (appointment && clientPack) throw new Error('Cannot pass appointment and pack at same time')

    useEffect(() => {
        if (fetchedClient && fetchedClient.id !== clientId) {
            setFetchedClient(null)
        }
    }, [clientId, fetchedClient])

    // Fetch client if not available or incomplete in Redux (e.g., when coming from appointments page)
    // Redux might have the client but with only id/name, missing subsystem data
    useEffect(() => {
        if (shouldFetchInvoiceTableClient(clientId, clientProp, clientFromRedux, fetchedClient, isLoadingClient)) {
            setIsLoadingClient(true);
            getClientAsync(clientId)
                .then(setFetchedClient)
                .finally(() => setIsLoadingClient(false));
        }
    }, [clientId, clientProp, clientFromRedux, fetchedClient, isLoadingClient])

    const closeModal = () => {
        setModalInvoice(null)
        setInvoiceNavigationHistory([])
        setCancelReason('')
        setConfirming(null)
        setConfirmingInvoice(null)
        setAssociatedDocumentId('')
        setIsFinalConsumer(false)
        setSelectedAppointmentIds([])
        setInvoiceFormPaymentMethod('')
        onModalClose?.()
    }

    const enrichInvoice = (invoice: Invoice): Invoice => {
        // Preserve local email state from the invoices list — the Cegid API
        // does not persist sentByEmail, so a GET fetch would lose it.
        const listEntry = invoices.find(i => i.id === invoice.id) as Partial<Invoice> | undefined

        return {
            ...invoice,
            clientId: invoice.clientId || clientId || client?.id,
            clientName: invoice.clientName || client?.name,
            address: invoice.address || client?.addressLine1,
            city: invoice.city || client?.city,
            country: invoice.country || 'Portugal',
            clientTaxNumber: invoice.clientTaxNumber || client?.taxNumber,
            sentByEmail: invoice.sentByEmail || listEntry?.sentByEmail,
            sentByEmailAt: [invoice.sentByEmailAt, listEntry?.sentByEmailAt]
                .filter(Boolean)
                .sort()
                .pop() || undefined,
        }
    }

    const openInvoice = (invoice: Invoice | InvoiceReduced) => {
        if (isLoadingInvoice) return

        // If invoice already has items (full Invoice), use it directly
        if ('items' in invoice && invoice.items?.length > 0) {
            setModalInvoice(enrichInvoice(invoice))
            return
        }

        // Otherwise, fetch the full invoice details
        setIsLoadingInvoice(true)
        getInvoiceAsync(invoice.id)
            .then(fullInvoice => setModalInvoice(enrichInvoice(fullInvoice)))
            .catch((error) => {
                const message = getErrorMessage(error)
                if (message) dispatch(alertError(message))
                else dispatch(alertGenericError())
            })
            .finally(() => setIsLoadingInvoice(false))
    }

    const ensureInvoiceDetails = async (invoice: Invoice | InvoiceReduced) => {
        if ('items' in invoice && invoice.items?.length > 0) {
            return enrichInvoice(invoice as Invoice)
        }
        const detailedInvoice = await getInvoiceAsync(invoice.id)
        return enrichInvoice(detailedInvoice)
    }

    const handleNavigateToRelatedDoc = (relatedDocId: string) => {
        if (isLoadingInvoice || !isInvoiceVisualization(modalInvoice)) return

        setIsLoadingInvoice(true)
        getInvoiceAsync(relatedDocId)
            .then(relatedInvoice => {
                // Add current invoice to history before navigating
                setInvoiceNavigationHistory([...invoiceNavigationHistory, modalInvoice])
                setModalInvoice(enrichInvoice(relatedInvoice))
            })
            .catch((error) => {
                const message = getErrorMessage(error)
                if (message) dispatch(alertError(message))
                else dispatch(alertGenericError())
            })
            .finally(() => setIsLoadingInvoice(false))
    }

    const handleNavigateBack = () => {
        if (invoiceNavigationHistory.length === 0) return
        const previousInvoice = invoiceNavigationHistory[invoiceNavigationHistory.length - 1]
        setModalInvoice(previousInvoice)
        setInvoiceNavigationHistory(invoiceNavigationHistory.slice(0, -1))
    }
    
    const handleGeneratePdf = (invoice: InvoiceReduced | Invoice) => () => {
        if(fetching) return
        setFetching(true)
        generateInvoicePdfAsync(invoice.id)
            .then(({url}) => {window.open(url)})
            .catch((error) => {
                const message = getErrorMessage(error)
                if (message) dispatch(alertError(message))
                else dispatch(alertGenericError())
            })
            .finally(() => setFetching(false))
    }

    const handleGenerateCreditNotePdf = (invoice: InvoiceReduced | Invoice) => () => {
        if(fetching) return
        const creditNoteId = 'relatedDocuments' in invoice ? invoice.relatedDocuments?.filter(d => d.type === InvoiceDocumentType.CreditNote)?.[0]?.id : undefined
        if (!creditNoteId) return
        setFetching(true)
        generateInvoicePdfAsync(String(creditNoteId))
            .then(({url}) => {window.open(url)})
            .catch((error) => {
                const message = getErrorMessage(error)
                if (message) dispatch(alertError(message))
                else dispatch(alertGenericError())
            })
            .finally(() => setFetching(false))
    }

    const handleSendByEmail = (invoice: Invoice | InvoiceReduced) => () => {
        const alreadySent = (invoice as Partial<Invoice>).sentByEmail
        if(alreadySent && 'items' in invoice) {
            setConfirmingInvoice(invoice as Invoice)
            setConfirming('resend')
            return
        }

        performSendByEmail(invoice)
    }

    const performSendByEmail = (invoice: Invoice | InvoiceReduced, skipFetchingGuard = false) => {
        if (fetching && !skipFetchingGuard) return
        if (!skipFetchingGuard) {
            setFetching(true)
        }
        ensureInvoiceDetails(invoice)
            .then(invoiceDetails => {
                const payload = {
                    type: invoiceDetails.type,
                    clientId: invoiceDetails.clientId || clientId || client?.id || '',
                    date: invoiceDetails.date,
                }

                return sendInvoiceByEmailAsync(invoiceDetails.id, payload).then(() => invoiceDetails)
            })
            .then((invoiceDetails) => {
                dispatch(alertSuccess(t(TK.invoiceSent)))
                const sentAt = new Date().toISOString()
                const updatedInvoice = {...invoiceDetails, sentByEmail: true, sentByEmailAt: sentAt}
                if (isInvoiceVisualization(modalInvoice) && modalInvoice.id === updatedInvoice.id) {
                    setModalInvoice(updatedInvoice)
                }
                onUpdate?.(ensureInvoiceReduced(updatedInvoice))
            })
            .catch((error) => {
                const serverMessage = getErrorMessage(error)
                dispatch(alertError(serverMessage ?? t(TK.notPossibleToSendInvoicePleaseConfirmClientEmail)))
            })
            .finally(() => {
                if (!skipFetchingGuard) {
                    setFetching(false)
                }
            })
    }

    const handleAddNew = useCallback(() => {
        setSelectedAppointmentIds([])
        // Pre-fill observations with subsystem info and general observations
        const subsystemObs = client?.subsystemName && client?.subsystemNumber
            ? `${client.subsystemName}:${client.subsystemNumber}`
            : '';
        const generalObs = applyAppointmentPlaceholders(invoiceSettings?.generalObservations || '', appointment, generalSettings?.services, client);
        const shouldAddDetails = !!invoiceSettings?.addAppointmentPackDetailsAutomaticallyOnInvoice;
        const packDetailsTemplate = invoiceSettings?.appointmentPackDetailsTemplate || DEFAULT_APPOINTMENT_PACK_DETAILS_TEMPLATE;
        const detailsObs = shouldAddDetails
            ? (appointment ? buildAppointmentNotes([appointment], packDetailsTemplate, generalSettings?.services, client) : (clientPack ? buildPackObservation(clientPack, t) : ''))
            : '';
        const observations = [subsystemObs, generalObs, detailsObs]
            .filter(s => !!s?.length)
            .join('\n');

        const invoiceBranchId = appointment?.branchId ?? (useBranches ? selectedBranch : undefined);
        const effectiveLegalInfo = resolveEffectiveLegalInfo(invoiceSettings, invoiceBranchId);

        const taxId = resolveSourceTaxId(
            appointment?.type,
            clientPack ? { packTemplateId: clientPack.packTemplateId, packVersion: clientPack.packVersion } : undefined,
            generalSettings?.services,
            packsSettings,
            effectiveLegalInfo?.taxId,
        )

        const taxRate = taxId ? (taxHook.taxRatesMap[taxId]?.rate ?? 0) : 0

        const invoice: InvoiceData = {
            clientId: client?.id,
            clientName: client?.name,
            address: client?.addressLine1,
            city: client?.city,
            country: 'Portugal',
            clientTaxNumber: client?.taxNumber,
            date: new Date().toISOString().substring(0, 10),
            currencyCode: 'EUR',
            branchId: invoiceBranchId,
            observations,
            type: InvoiceDocumentType.InvoiceReceipt,
            payments: appointment?.payments ?? clientPack?.payments ?? undefined,
            sendEmail: invoiceSettings?.sendInvoicesAutomaticallyToClientsByEmail ?? false,
            appointmentId: appointment?.id,
            packId: clientPack?.id,
            globalDiscountAmount: (appointment?.expenses ?? clientPack?.extras ?? [])
                .filter(e => e.value < 0)
                .reduce((sum, curr) => sum - curr.value*curr.quantity, 0) * ((100 + taxRate) / 100),
            items: appointment ? [
                // Main service item
                ((): InvoiceItem => {
                    const invoiceItemDetails = generalSettings.services?.[appointment.type]?.invoiceItemDetails;
                    const { taxId, taxExemption, taxExemptionLaw } = resolveTaxDetails(
                        invoiceItemDetails,
                        effectiveLegalInfo,
                    );

                    const services = generalSettings.services;
                    const serviceDisplayName = services?.[appointment.type]?.name || appointment.type;
                    const rawTitle = invoiceItemDetails?.customTitle || serviceDisplayName;
                    const rawDescription = invoiceItemDetails?.customDescription || serviceDisplayName;
                    return {
                        code: applyAppointmentPlaceholders(rawTitle, appointment, services, client),
                        description: applyAppointmentPlaceholders(rawDescription, appointment, services, client),
                        quantity: 1,
                        unitPrice: appointment.price,
                        taxId,
                        typeId: invoiceItemDetails?.type || InvoiceItemType.S,
                        taxExemption,
                        taxExemptionLaw,
                    };
                })(),
                // Expense/extra items - grouped by name and value
                ...mapExtrasToInvoiceItems(
                    appointment.expenses ?? [],
                    extrasSettings,
                    effectiveLegalInfo,
                    appointment,
                    generalSettings.services,
                    client,
                )
            ] : clientPack ? [
                ((): InvoiceItem => {
                    // Get invoice item details from pack template (source of truth)
                    // Always use the LATEST version of the template (highest version number)
                    const packTemplates = packsSettings.filter(p => (p.templateId ?? p.id) === clientPack.packTemplateId);
                    const packTemplate = packTemplates.sort((a, b) => b.version - a.version)[0];
                    const invoiceItemDetails = packTemplate?.invoiceItemDetails;

                    const { taxId, taxExemption, taxExemptionLaw } = resolveTaxDetails(
                        invoiceItemDetails,
                        effectiveLegalInfo,
                    );

                    return {
                        code: invoiceItemDetails?.customTitle || clientPack.packName || t(TK.pack),
                        description: invoiceItemDetails?.customDescription || clientPack.packName || `${t(TK.pack)}/${t(TK.subscription)}`,
                        quantity: 1,
                        unitPrice: clientPack.price,
                        taxId,
                        typeId: invoiceItemDetails?.type || InvoiceItemType.P,
                        taxExemption,
                        taxExemptionLaw,
                    };
                })(),
                // Expense/extra items - grouped by name and value
                ...mapExtrasToInvoiceItems(
                    clientPack.extras ?? [],
                    extrasSettings,
                    effectiveLegalInfo
                )
            ] : [],
        }
        setModalInvoice(invoice)
        // Store original payments from appointment/pack to prevent stacking on retry
        setOriginalPaymentsFromSource(invoice.payments || [])
        // Pre-fill payment method from invoice settings default when there are no existing payments
        setInvoiceFormPaymentMethod(resolveInitialInvoiceFormPaymentMethod(invoice.payments, createPaymentMethod ?? invoiceSettings?.defaultPaymentMethod))
    }, [clientId, client, appointment, clientPack, generalSettings, extrasSettings, invoiceSettings, packsSettings, taxHook.getTaxNameFromTaxId, t, createPaymentMethod])

    useEffect(() => {
        // Wait for tax data to load before creating invoice to ensure correct tax rates are applied.
        // Allow creation even when no client was pre-selected (standalone invoice flow).
        const isTaxDataReady = !taxHook.isLoading && taxHook.taxRates.length > 0;
        const isClientDataReady = (!!client || !clientId) && !isLoadingClient;
        if (createNew && !modalInvoice && isTaxDataReady && isClientDataReady) {
            handleAddNew()
        }
    }, [createNew, modalInvoice, taxHook.isLoading, taxHook.taxRates.length, client, clientId, isLoadingClient, handleAddNew])

    const [autoEmittedReceiptForId, setAutoEmittedReceiptForId] = useState<string | null>(null)
    useEffect(() => {
        if (!emitReceiptForInvoiceId) {
            if (autoEmittedReceiptForId) setAutoEmittedReceiptForId(null)
            return
        }
        if (autoEmittedReceiptForId === emitReceiptForInvoiceId) return
        if (modalInvoice || isLoadingInvoice || fetching) return
        setAutoEmittedReceiptForId(emitReceiptForInvoiceId)
        setIsLoadingInvoice(true)
        getInvoiceAsync(emitReceiptForInvoiceId)
            .then(full => {
                const enriched = enrichInvoice(full)
                setModalInvoice(enriched)
                setConfirmingInvoice(enriched)
                setReceiptAmount(enriched.total)
                setReceiptPaymentMethod(paymentMethods[0] || '')
                setReceiptPaymentDate(moment().format('YYYY-MM-DD'))
                setSendReceiptByEmail(invoiceSettings?.sendInvoicesAutomaticallyToClientsByEmail ?? false)
                withRegisterCheck(() => setConfirming('receipt'))
            })
            .catch((error) => {
                const message = getErrorMessage(error)
                if (message) dispatch(alertError(message))
                else dispatch(alertGenericError())
                setAutoEmittedReceiptForId(null)
            })
            .finally(() => setIsLoadingInvoice(false))
    }, [emitReceiptForInvoiceId, autoEmittedReceiptForId, modalInvoice, isLoadingInvoice, fetching, paymentMethods, invoiceSettings])

    const handleCreateNewInvoice = () => {
        if (!modalInvoice) return
        if (fetching) return

        const isCreditNote = modalInvoice.type === InvoiceDocumentType.CreditNote
        const isInvoiceReceipt = modalInvoice.type === InvoiceDocumentType.InvoiceReceipt
            || modalInvoice.type === InvoiceDocumentType.Receipt
        const invoiceTotal = calculateInvoiceTotal(modalInvoice, taxHook.taxRatesMap)

        const hasEmptyItemCodes = (modalInvoice.items ?? []).some(item => !(item.code?.trim()))
        if ((modalInvoice.items?.length ?? 0) === 0 || hasEmptyItemCodes) {
            dispatch(alertError(t(TK.invoiceItemCodeRequired)))
            return
        }

        if (isCreditNote && !associatedDocumentId) {
            return
        }

        // Check for in-progress payments and filter to only completed payments (Manual/Success) for invoice receipts
        const allPayments: Payment[] = isInvoiceReceipt
            ? ((appointment || clientPack) ? originalPaymentsFromSource : (modalInvoice.payments ?? []))
            : []
        const completedPayments = isInvoiceReceipt
            ? allPayments.filter(isCompletedInvoicePayment)
            : []
        const inProgressPayments = isInvoiceReceipt
            ? allPayments.filter(p => p.status === PaymentStatus.InProgress)
            : []
        const automaticReceiptPayment = isInvoiceReceipt
            ? buildAutomaticInvoiceReceiptPayment({
                invoiceTotal,
                paymentMethod: invoiceFormPaymentMethod,
                isFromAppointmentOrPack: !!(appointment || clientPack),
                modalPayments: modalInvoice.payments,
                sourcePayments: originalPaymentsFromSource,
                cashRegisterName: selectedCashRegisterName || undefined,
            })
            : null

        const openPaymentValidationModal = (payments: Payment[] = []) => {
            setPaymentValidationData({
                invoiceTotal,
                originalTotal: invoiceTotal,
                difference: invoiceTotal,
                payments,
            })
            setSelectedPaymentMethod(invoiceFormPaymentMethod || paymentMethods[0] || '')
            setShowPaymentValidationModal(true)
        }

        // If there are no payments, show modal to select payment method before proceeding with invoice receipt emission
        if (isInvoiceReceipt && inProgressPayments.length > 0) {
            if (completedPayments.length === 0) {
                openPaymentValidationModal()
                return
            }
        }

        // Validation for appointment/pack invoice receipt - must have completed payment
        if ((appointment || clientPack) && isInvoiceReceipt) {
            if (completedPayments.length === 0) {
                if (automaticReceiptPayment) {
                    proceedWithInvoiceEmission({
                        ...modalInvoice,
                        payments: [automaticReceiptPayment],
                    })
                    return
                }
                openPaymentValidationModal()
                return
            }
        }

        // Validation for standalone invoice with receipt - must have payment method
        if (!appointment && !clientPack && isInvoiceReceipt) {
            const hasPayments = modalInvoice.payments && modalInvoice.payments.length > 0
            if (!hasPayments) {
                if (automaticReceiptPayment) {
                    proceedWithInvoiceEmission({
                        ...modalInvoice,
                        payments: [automaticReceiptPayment],
                    })
                    return
                }
                openPaymentValidationModal()
                return
            }
        }

        // Validation for appointment/pack invoices - check for value mismatch
        if ((appointment || clientPack) && isInvoiceReceipt) {
            // Calculate original total WITH VAT to match invoice total
            // Get the primary tax rate from the first invoice item (main service/pack)
            const primaryTaxId = modalInvoice.items?.[0]?.taxId
            const primaryTaxRate = primaryTaxId ? (taxHook.taxRatesMap[primaryTaxId]?.rate ?? 0) : 0

            const originalTotalWithVAT = appointment
                ? calculateAppointmentTotalWithVAT(appointment, primaryTaxRate)
                : (clientPack ? calculateClientPackTotalWithVAT(clientPack, primaryTaxRate) : 0)

            // Compare invoice total WITH VAT to original total WITH VAT
            const difference = invoiceTotal - originalTotalWithVAT

            // If difference is positive (invoice > original), show modal to select payment method for the extra
            if (difference > 0.01) {
                setPaymentValidationData({
                    invoiceTotal: invoiceTotal,
                    originalTotal: originalTotalWithVAT,
                    difference: difference,
                    payments: completedPayments // Use only completed payments
                })
                setSelectedPaymentMethod(invoiceFormPaymentMethod || paymentMethods[0] || '')
                setShowPaymentValidationModal(true)
                return
            }

            // If difference is negative (invoice < original), use the appointment's payment method with the invoice total
            if (difference < -0.01) {
                const existingPaymentMethod = completedPayments[0]?.type || paymentMethods[0] || ''
                proceedWithInvoiceEmission({
                    ...modalInvoice!,
                    payments: [buildManualInvoicePayment(existingPaymentMethod, invoiceTotal, undefined, selectedCashRegisterName || undefined)],
                })
                return
            }
        }

        // If all validations pass, proceed with emission
        proceedWithInvoiceEmission()
    }

    const proceedWithInvoiceEmission = (invoiceDataOverride?: InvoiceData) => {
        const invoiceToUse = invoiceDataOverride || modalInvoice
        if (!invoiceToUse) return
        if (fetching) return

        setFetching(true)

        const isCreditNote = invoiceToUse.type === InvoiceDocumentType.CreditNote

        // Filter payments to only include completed payments (Manual/Success)
        const filteredPayments = invoiceToUse.payments?.filter(p =>
            p.status === PaymentStatus.Manual || p.status === PaymentStatus.Success
        )

        // Transform invoice data for Final Consumer mode
        const invoiceToSubmit: InvoiceData = isFinalConsumer
            ? {
                ...invoiceToUse,
                clientName: undefined,
                address: undefined,
                city: undefined,
                country: undefined,
                clientTaxNumber: undefined,
                payments: filteredPayments,
            }
            : {
                ...invoiceToUse,
                payments: filteredPayments,
            }

        let promise: Promise<Invoice>

        if (isCreditNote && associatedDocumentId) {
            const creditNoteData: CreditNoteData = {
                originalInvoiceId: associatedDocumentId,
                reason: invoiceToUse.observations || 'Credit note',
                branchId: invoiceToUse.branchId,
                appointmentId: appointment?.id,
                packId: clientPack?.id,
            }
            promise = issueCreditNoteAsync(associatedDocumentId, creditNoteData)
        } else if (appointment) {
            promise = createInvoiceForAppointmentAsync(appointment.id, invoiceToSubmit)
        } else if (clientPack) {
            promise = createInvoiceForPackAsync(clientPack.id, invoiceToSubmit)
        } else if (selectedAppointmentIds.length > 0) {
            const invoiceAppointmentsData: InvoiceManyAppointmentsData = {
                ...invoiceToSubmit,
                appointmentIds: selectedAppointmentIds,
            }
            promise = createInvoiceForAppointmentsAsync(invoiceAppointmentsData)
        } else {
            promise = createStandaloneInvoiceAsync(invoiceToSubmit)
        }

        promise.then((invoice: Invoice) => {
            dispatch(alertSuccess(t(TK.created)))
            // Use the total returned from the API (already includes VAT and discounts)
            onCreated && onCreated(toInvoiceReduced(invoice))
            syncSourceEntityAfterEmission(appointment, clientPack, client?.id, {
                dispatch,
                getAppointment: (id) => getAppointmentAsync(id) as Promise<Entity>,
                getClientPack: (cId, pId) => getClientPackAsync(cId, pId) as Promise<Entity>,
            })
            closeModal()
        })
        .catch((error) => {
            const serverMessage = getErrorMessage(error)
            dispatch(alertError(serverMessage ?? t(TK.notPossibleToCreateInvoicePleaseConfirmClientData)))
        })
        .finally(() => setFetching(false))
    }

    const handlePaymentValidationConfirm = () => {
        if (!modalInvoice || !paymentValidationData) return

        // Create updated payments array
        let updatedPayments: Payment[] = [...paymentValidationData.payments]

        // If difference is positive (invoice > original), add a new payment for the difference
        if (paymentValidationData.difference > 0.01) {
            updatedPayments = [...updatedPayments, buildManualInvoicePayment(selectedPaymentMethod, paymentValidationData.difference, undefined, selectedCashRegisterName || undefined)]
        }
        // If difference is negative (invoice < original), adjust existing payments or remove the difference
        else if (paymentValidationData.difference < -0.01) {
            const absExcess = Math.abs(paymentValidationData.difference)
            let remaining = absExcess

            // Remove payments starting from the end until we've accounted for the excess
            updatedPayments = []
            for (const payment of paymentValidationData.payments) {
                if (remaining <= 0) {
                    updatedPayments.push(payment)
                } else if (payment.value <= remaining) {
                    remaining -= payment.value
                    // Skip this payment (don't add to updatedPayments)
                } else {
                    // Reduce this payment's value (create new object, don't mutate)
                    updatedPayments.push({
                        ...payment,
                        value: payment.value - remaining
                    })
                    remaining = 0
                }
            }
        }

        // For standalone invoice receipt, just add the payment
        if (!appointment && !clientPack && paymentValidationData.originalTotal === paymentValidationData.invoiceTotal) {
            updatedPayments = [buildManualInvoicePayment(selectedPaymentMethod, paymentValidationData.invoiceTotal, undefined, selectedCashRegisterName || undefined)]
        }

        // Create the updated invoice data with new payments
        const updatedInvoiceData: InvoiceData = {
            ...modalInvoice,
            payments: updatedPayments
        }

        // Update the modal invoice state for UI consistency
        setModalInvoice(updatedInvoiceData)

        // Close payment validation modal
        setShowPaymentValidationModal(false)
        setPaymentValidationData(null)
        setSelectedPaymentMethod('')

        // Proceed with emission using the updated invoice data
        proceedWithInvoiceEmission(updatedInvoiceData)
    }

    const handleFinalizeInvoice = (invoice: Invoice | InvoiceReduced) => () => {
        setConfirmingInvoice(invoice)
        setConfirming('finalize')
    }
    const handleCancelInvoice = (invoice: Invoice | InvoiceReduced) => () => {
        setConfirmingInvoice(invoice)
        setConfirming('cancel')
    }
    const handleEmitReceipt = (invoice: Invoice | InvoiceReduced) => () => {
        setConfirmingInvoice(invoice)
        setReceiptAmount(invoice.total)
        setReceiptPaymentMethod(paymentMethods[0] || '')
        setReceiptPaymentDate(moment().format('YYYY-MM-DD'))
        setSendReceiptByEmail(invoiceSettings?.sendInvoicesAutomaticallyToClientsByEmail ?? false)
        withRegisterCheck(() => setConfirming('receipt'))
    }
    const handleDeleteInvoice = (invoice: Invoice | InvoiceReduced) => () => {
        setConfirmingInvoice(invoice)
        setConfirming('delete')
    }

    const handleOpenAppointment = (invoice: Invoice | InvoiceReduced) => () => {
        const appointmentId = invoice.appointmentId
        if (!appointmentId) return
        dispatch(openEntity<AppointmentEntity>(
            'appointments',
            appointmentId,
            () => getAppointmentAsync(appointmentId)
        ))
    }

    const handleOpenPack = (invoice: Invoice | InvoiceReduced) => () => {
        if (!invoice.packId) return
        const invoiceClientId = invoice.clientId || client?.id
        if (!invoiceClientId) {
            console.error('Cannot open pack: clientId not available', { invoice, client })
            return
        }

        dispatch(openEntity<ClientPackEntity>(
            'clientPacks',
            invoice.packId,
            () => getClientPackAsync(invoiceClientId, invoice.packId!)
        ))
    }

    const handleActionConfimation = () => {
        const invoice = confirmingInvoice || (isInvoiceVisualization(modalInvoice) ? modalInvoice : null)

        if (fetching) return
        if (!invoice) return

        if (confirming === 'resend') {
            setConfirming(null)
            setConfirmingInvoice(null)
            performSendByEmail(invoice)
            return
        }

        if (confirming === 'cancel') {
            const creditNote: CreditNoteData = {
                originalInvoiceId: invoice.id,
                reason: cancelReason,
                branchId: invoice.branchId,
                appointmentId: appointment?.id,
                packId: clientPack?.id,
            }

            setFetching(true)
            issueCreditNoteAsync(invoice.id, creditNote)
                .then((updatedInvoice) => {
                    dispatch(alertSuccess(t(TK.canceled)))
                    // API now returns the original invoice with updated state and relatedDocuments
                    onUpdate?.(ensureInvoiceReduced(enrichInvoice(updatedInvoice)))
                    setConfirming(null)
                    setConfirmingInvoice(null)
                    setCancelReason('')
                    closeModal()
                })
                .catch((error) => {
                    console.error('Error canceling invoice:', error)
                    const serverMessage = getErrorMessage(error)
                    dispatch(alertError(serverMessage ?? t(TK.somethingWentWrong)))
                })
                .finally(() => setFetching(false))

            return
        }

        if (confirming === 'receipt') {
            const appointmentId = invoice.appointmentId || appointment?.id
            const packId = invoice.packId || clientPack?.id
            const receiptData: MarkInvoicePaidData = {
                paymentMethod: receiptPaymentMethod,
                paymentDate: receiptPaymentDate,
                amount: receiptAmount,
                sendEmail: sendReceiptByEmail,
                branchId: invoice.branchId,
                appointmentId,
                packId,
                cashRegisterName: selectedCashRegisterName || undefined,
            }

            setFetching(true)
            emitInvoiceReceiptAsync(invoice.id, receiptData)
                .then((updatedInvoice) => {
                    dispatch(alertSuccess(t(TK.receiptEmitted)))
                    const enrichedUpdatedInvoice = enrichInvoice(updatedInvoice)
                    onUpdate?.(ensureInvoiceReduced(enrichedUpdatedInvoice))
                    onReceiptEmitted?.()
                    setModalInvoice(enrichedUpdatedInvoice)
                    const receiptDocId = enrichedUpdatedInvoice.relatedDocuments?.find(doc => doc.type === InvoiceDocumentType.Receipt)?.id

                    syncSourceEntityAfterEmission(appointment, clientPack, client?.id, {
                        dispatch,
                        getAppointment: (id) => getAppointmentAsync(id) as Promise<Entity>,
                        getClientPack: (cId, pId) => getClientPackAsync(cId, pId) as Promise<Entity>,
                    })

                    setConfirming(null)
                    setConfirmingInvoice(null)
                    setReceiptPaymentMethod('')
                    setReceiptPaymentDate(moment().format('YYYY-MM-DD'))
                    setReceiptAmount(0)
                    setSendReceiptByEmail(false)

                    if (receiptDocId) {
                        setIsLoadingInvoice(true)
                        setInvoiceNavigationHistory(prev => [...prev, enrichedUpdatedInvoice])
                        getInvoiceAsync(receiptDocId.toString())
                            .then(relatedInvoice => {
                                const enrichedRelatedInvoice = enrichInvoice(relatedInvoice)
                                setModalInvoice(enrichedRelatedInvoice)
                            })
                            .catch((error) => {
                                const message = getErrorMessage(error)
                                if (message) dispatch(alertError(message))
                                else dispatch(alertGenericError())
                            })
                            .finally(() => setIsLoadingInvoice(false))
                    }
                })
                .catch((error) => {
                    console.error('Error emitting receipt:', error)
                    const serverMessage = getErrorMessage(error)
                    dispatch(alertError(serverMessage ?? t(TK.somethingWentWrong)))
                })
                .finally(() => setFetching(false))

            return
        }

        if (confirming === 'delete') {
            const data: ChangeInvoiceState = {
                type: invoice.type,
                message: cancelReason,
                newState: InvoiceState.Canceled,
                branchId: invoice.branchId,
                appointmentId: appointment?.id,
                packId: clientPack?.id,
            }

            setFetching(true)
            updateInvoiceStateAsync(invoice.id, data)
                .then(() => {
                    dispatch(alertSuccess(t(TK.deleted)))
                    const updatedInvoice = { ...invoice, state: InvoiceState.Canceled } as Invoice | InvoiceReduced
                    onUpdate?.(ensureInvoiceReduced(updatedInvoice))
                    setConfirming(null)
                    setConfirmingInvoice(null)
                    setCancelReason('')
                    closeModal()
                })
                .catch((error) => {
                    console.error('Error deleting draft invoice:', error)
                    const message = getErrorMessage(error)
                    dispatch(alertError(message ?? t(TK.somethingWentWrong)))
                })
                .finally(() => setFetching(false))

            return
        }

        // Finalize draft invoice by changing state to Issued
        const data: ChangeInvoiceState = {
            type: invoice.type,
            message: '',
            newState: InvoiceState.Issued,
            branchId: invoice.branchId,
            appointmentId: appointment?.id,
            packId: clientPack?.id,
        }

        setFetching(true)
        updateInvoiceStateAsync(invoice.id, data)
            .then(() => {
                dispatch(alertSuccess(t(TK.update)))
                const updatedInvoice = { ...invoice, state: InvoiceState.Issued } as Invoice | InvoiceReduced
                onUpdate?.(ensureInvoiceReduced(updatedInvoice))
                setConfirming(null)
                setConfirmingInvoice(null)
                closeModal()
            })
            .catch((error) => {
                console.error('Error finalizing invoice:', error)
                const message = getErrorMessage(error)
                dispatch(alertError(message ?? t(TK.somethingWentWrong)))
            })
            .finally(() => setFetching(false))
    }

    return (
        <>
            { onCreated && !hideCreateButton && !userPermissions.includes(PermissionEnum.DoNotAllowToEmitInvoices) && createButtonStyle === 'button' &&
                <div style={{display: 'flex', justifyContent: 'flex-end', margin: 'var(--spacer-S) 0'}}>
                    <AppButton color="success" onClick={handleAddNew}>
                        {createButtonLabel || t(TK.emitStandaloneInvoice)}
                    </AppButton>
                </div>
            }
            { loading ? <Typography><i>{t(TK.loading)}...</i></Typography> :
              !invoices?.length && !onCreated ? <Typography><i>{t(TK.empty)}</i></Typography> :
                <ListGroup>
                    { invoices.map(invoice => {
                        const fullInvoice = invoice as Partial<Invoice>
                        const sentByEmail = fullInvoice.sentByEmail ?? false
                        const sentByEmailAt = fullInvoice.sentByEmailAt
                        const hasClientEmail = !!client?.email?.trim()
                        const branchLabel = getBranchDisplayName(invoice.branchId, generalSettings?.branches, t)
                        const emailTooltip = !hasClientEmail
                            ? t(TK.clientEmailRequiredToSendInvoice)
                            : sentByEmail && sentByEmailAt
                                ? `${t(TK.emailSentToClientOn)} ${moment(sentByEmailAt).format('DD/MM/YYYY HH:mm')}`
                                : t(TK.sendInvoiceToClientEmail);
                        const creditNoteId = fullInvoice.relatedDocuments?.filter(d => d.type === InvoiceDocumentType.CreditNote)?.[0]?.id
                        const receiptDocs = fullInvoice.relatedDocuments?.filter(d => d.type === InvoiceDocumentType.Receipt) ?? []

                        const isPendingReceipt = highlightPendingReceipt
                            && (invoice as InvoiceReduced).hasReceipt === false
                            && (invoice.type === InvoiceDocumentType.Invoice || invoice.type === InvoiceDocumentType.SimplifiedInvoice)
                            && invoice.state === InvoiceState.Issued

                        return (
                            <React.Fragment key={invoice.id}>
                                <ListGroupItem style={{display: 'flex', alignItems: 'center', gap: 'var(--spacer-S)', backgroundColor: isPendingReceipt ? 'var(--warning-bg)' : undefined}}>
                                    <div style={{flexGrow: 1, borderRight: '1px solid #AAA', paddingRight: 'var(--spacer-S)'}}>
                                        {invoiceDescription(invoice, t, () => openInvoice(invoice), branchLabel, showClientName)}
                                    </div>
                                    <div style={{display: 'flex', gap: 'var(--spacer-XS)', alignItems: 'center', minWidth: compactActionButtons ? '72px' : '93px', justifyContent: 'end'}}>
                                        {showNavigationButtons && invoice.appointmentId && (
                                            <IconButton size='small' id={`open-appointment-button-${invoice.id}`} disabled={fetching} onClick={handleOpenAppointment(invoice)}><EventIcon/></IconButton>
                                        )}
                                        {showNavigationButtons && invoice.packId && (
                                            <IconButton size='small' id={`open-pack-button-${invoice.id}`} disabled={fetching} onClick={handleOpenPack(invoice)}><LocalOfferIcon/></IconButton>
                                        )}
                                        {!compactActionButtons && (
                                            <>
                                                <IconButton size='small' id={`finalize-invoice-button-${invoice.id}`} disabled={fetching} onClick={handleFinalizeInvoice(invoice)} style={hide(invoice.state !== InvoiceState.Draft)}><CheckIcon/></IconButton>
                                                <IconButton size='small' id={`cancel-invoice-button-${invoice.id}`}
                                                    disabled={fetching}
                                                    onClick={handleCancelInvoice(invoice)}
                                                    style={{color: '#dc3545', ...hide(!('relatedDocuments' in invoice)
                                                        || invoice.state === InvoiceState.Draft
                                                        || invoice.state === InvoiceState.Canceled
                                                        || invoice.type === InvoiceDocumentType.CreditNote
                                                        || invoice.type === InvoiceDocumentType.Receipt
                                                        || !!creditNoteId)}}><CloseIcon/></IconButton>
                                                <IconButton size='small' id={`delete-invoice-button-${invoice.id}`} disabled={fetching} onClick={handleDeleteInvoice(invoice)} style={{color: '#dc3545', ...hide(invoice.state !== InvoiceState.Draft)}}><DeleteIcon /></IconButton>
                                                {!hideEmailButton && (
                                                    <span id={`send-email-button-${invoice.id}`} style={{display: 'inline-flex'}}>
                                                        <IconButton size='small' disabled={fetching || !hasClientEmail} onClick={handleSendByEmail(invoice)} style={{color: sentByEmail ? 'green' : undefined}}><MailIcon/></IconButton>
                                                    </span>
                                                )}
                                            </>
                                        )}
                                        <IconButton size='small' id={`generate-pdf-button-${invoice.id}`} disabled={fetching} onClick={handleGeneratePdf(invoice)}><DownloadIcon/></IconButton>
                                        {!compactActionButtons && (
                                            <IconButton size='small' id={`generate-credit-note-pdf-button-${invoice.id}`} disabled={fetching} onClick={handleGenerateCreditNotePdf(invoice)} style={{color: undefined, ...hide(invoice.state !== InvoiceState.Canceled || !creditNoteId)}}><CreditCardIcon/></IconButton>
                                        )}
                                    </div>
                                </ListGroupItem>
                                {receiptDocs.map(receipt => (
                                    <ListGroupItem
                                        key={`receipt-${receipt.id}`}
                                        style={{display: 'flex', alignItems: 'center', gap: 'var(--spacer-S)', paddingLeft: 'var(--spacer-L)', backgroundColor: 'var(--bg-light)', cursor: 'pointer'}}
                                        onClick={() => openInvoice({ id: receipt.id, type: receipt.type, state: InvoiceState.Issued, total: Number(receipt.amount ?? 0), date: '', number: receipt.number } as InvoiceReduced)}
                                    >
                                        <ReceiptIcon fontSize="small" style={{color: 'var(--text-muted)'}}/>
                                        <div style={{flexGrow: 1, display: 'flex', flexDirection: 'column'}}>
                                            <Typography size='small'><b>{getInvoiceTypeText(receipt, t)}</b>{receipt.number ? ` #${receipt.number}` : ''}</Typography>
                                        </div>
                                        <Typography size='small' style={{color: invoiceStateColor(InvoiceState.Issued, InvoiceDocumentType.Receipt)}}>
                                            <b>{formatEuros(Number(receipt.amount ?? 0))}</b>
                                        </Typography>
                                        <IconButton size='small' disabled={fetching} onClick={(e) => { e.stopPropagation(); handleGeneratePdf({ id: receipt.id, type: receipt.type, state: InvoiceState.Issued, total: Number(receipt.amount ?? 0), date: '', number: receipt.number } as InvoiceReduced)(); }}><DownloadIcon/></IconButton>
                                    </ListGroupItem>
                                ))}
                                {showNavigationButtons && invoice.appointmentId && (
                                    <ToolTip target={`open-appointment-button-${invoice.id}`} toolTipContent={t(TK.openAppointment)}/>
                                )}
                                {showNavigationButtons && invoice.packId && (
                                    <ToolTip target={`open-pack-button-${invoice.id}`} toolTipContent={t(TK.openPack)}/>
                                )}
                                {!compactActionButtons && !hideEmailButton && (
                                    <ToolTip target={`send-email-button-${invoice.id}`} toolTipContent={emailTooltip}/>
                                )}
                                <ToolTip target={`generate-pdf-button-${invoice.id}`} toolTipContent={t(TK.generateInvoicePdfFile)}/>
                                {!compactActionButtons && (
                                    <>
                                        <ToolTip target={`generate-credit-note-pdf-button-${invoice.id}`} toolTipContent={t(TK.downloadCreditNotePdf)}/>
                                        <ToolTip target={`finalize-invoice-button-${invoice.id}`} toolTipContent={t(TK.finalize)}/>
                                        <ToolTip target={`cancel-invoice-button-${invoice.id}`} toolTipContent={t(TK.cancelInvoice)}/>
                                        <ToolTip target={`delete-invoice-button-${invoice.id}`} toolTipContent={t(TK.deleteDraft)}/>
                                    </>
                                )}
                            </React.Fragment>
                        );
                    })}
                </ListGroup>
            }
            { onCreated && !hideCreateButton && !userPermissions.includes(PermissionEnum.DoNotAllowToEmitInvoices) && createButtonStyle === 'icon' &&
                <div>
                    <IconButton id="issue-invoice-button" onClick={handleAddNew}>
                        <ReceiptIcon />
                    </IconButton>
                    <ToolTip target='issue-invoice-button' toolTipContent={t(TK.issueInvoice)}/>
                </div>
            }

            <Modal size="xl" isOpen={!!modalInvoice}>
                { modalInvoice && (
                    <>
                        {invoiceNavigationHistory.length > 0 && (
                            <div style={{
                                padding: 'var(--spacer-S)',
                                borderBottom: '1px solid #dee2e6',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacer-S)',
                                backgroundColor: '#f8f9fa'
                            }}>
                                <IconButton
                                    onClick={handleNavigateBack}
                                    size="small"
                                    style={{ padding: '0.25rem' }}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                                    </svg>
                                </IconButton>
                                <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                                    {invoiceNavigationHistory.map((inv, idx) => (
                                        <span key={idx}>
                                            {idx > 0 && ' → '}
                                            {getInvoiceTypeText(inv, t)} #{inv.number}
                                        </span>
                                    ))}
                                    {isInvoiceVisualization(modalInvoice) && (
                                        <>
                                            {' → '}
                                            <strong>{getInvoiceTypeText(modalInvoice, t)} #{modalInvoice.number}</strong>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                        <InvoiceForm
                            value={modalInvoice}
                            clientEmail={client?.email}
                            fetching={fetching}
                            taxHook={taxHook}
                            showNavigationButtons={showNavigationButtons}
                            allowCreditNote={!!allowCreditNoteCreation}
                            isFinalConsumer={isFinalConsumer}
                            onFinalConsumerChange={setIsFinalConsumer}
                            onChange={setModalInvoice}
                            onClose={closeModal}
                            onSubmit={isInvoiceCreation(modalInvoice) ? handleCreateNewInvoice : undefined}
                            onGeneratePdf={isInvoiceVisualization(modalInvoice) ? handleGeneratePdf(modalInvoice) : undefined}
                            onSendByEmail={isInvoiceVisualization(modalInvoice) ? handleSendByEmail(modalInvoice) : undefined}
                            onFinalize={isInvoiceVisualization(modalInvoice) ? handleFinalizeInvoice(modalInvoice) : undefined}
                            onCancel={isInvoiceVisualization(modalInvoice) ? handleCancelInvoice(modalInvoice) : undefined}
                            onDelete={isInvoiceVisualization(modalInvoice) ? handleDeleteInvoice(modalInvoice) : undefined}
                            onEmitReceipt={isInvoiceVisualization(modalInvoice) ? handleEmitReceipt(modalInvoice) : undefined}
                            onNavigateToRelatedDoc={handleNavigateToRelatedDoc}
                            onOpenAppointment={isInvoiceVisualization(modalInvoice) ? handleOpenAppointment(modalInvoice) : undefined}
                            onOpenPack={isInvoiceVisualization(modalInvoice) ? handleOpenPack(modalInvoice) : undefined}
                            onAssociatedDocumentChange={setAssociatedDocumentId}
                            appointmentIds={selectedAppointmentIds}
                            onAppointmentIdsChange={setSelectedAppointmentIds}
                            paymentMethod={invoiceFormPaymentMethod}
                            onPaymentMethodChange={setInvoiceFormPaymentMethod}
                            paymentMethodOptions={paymentMethods}
                        />
                    </>
                )}
            </Modal>

            <Modal isOpen={!!confirming}>
                <ModalHeader toggle={() => setConfirming(null)}>{t(TK.areYouSure)}</ModalHeader>
                {confirming === 'resend' &&
                    <ModalBody>
                        <Typography size='small' style={{ marginBottom: 'var(--spacer-S)' }}>
                            {t(TK.invoiceAlreadySent)}
                        </Typography>
                        {(confirmingInvoice as Partial<Invoice>)?.sentByEmailAt && (
                            <Typography size='small' style={{ color: '#666', marginTop: 'var(--spacer-XS)' }}>
                                {"\n" + t(TK.emailSentToClientOn)} {moment((confirmingInvoice as Partial<Invoice>).sentByEmailAt).format('DD/MM/YYYY HH:mm') +"."}
                            </Typography>
                        )}
                    </ModalBody>
                }
                {confirming === 'cancel' &&
                    <ModalBody>
                        <Typography size='small' style={{ marginBottom: 'var(--spacer-S)' }}>
                            {t(TK.cancelInvoice)} - {t(TK.aCreditNoteWillBeIssued)}
                        </Typography>
                        <FormField label={t(TK.reason)}><TextInputField
                            type='textarea'
                            value={cancelReason}
                            onChange={setCancelReason}
                        /></FormField>
                    </ModalBody>
                }
                {confirming === 'delete' &&
                    <ModalBody>
                        <Typography size='small' style={{ marginBottom: 'var(--spacer-S)' }}>
                            {t(TK.deleteDraft)}
                        </Typography>
                        <FormField label={t(TK.reason)}><TextInputField
                            type='textarea'
                            value={cancelReason}
                            onChange={setCancelReason}
                        /></FormField>
                    </ModalBody>
                }
                {confirming === 'receipt' &&
                    <ModalBody>
                        <Typography size='small' style={{ marginBottom: 'var(--spacer-M)' }}>
                            {t(TK.confirmReceiptEmission)
                                .replace('{invoiceNumber}', confirmingInvoice?.number || '')
                            }
                        </Typography>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                            <div style={{ display: 'flex', gap: 'var(--spacer-S)' }}>
                                <div style={{ flex: 1 }}>
                                    <NumberInputField
                                        value={receiptAmount}
                                        prepend='€'
                                        onChange={(val) => setReceiptAmount(val ?? 0)}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <AutocompleteTextField
                                        value={receiptPaymentMethod}
                                        options={paymentMethods}
                                        onValueChanged={setReceiptPaymentMethod}
                                    />
                                </div>
                            </div>
                            <TextInputField
                                type='date'
                                value={receiptPaymentDate}
                                onChange={setReceiptPaymentDate}
                            />
                            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XXS)', marginBottom: 0 }}>
                                <CheckInputField
                                    value={sendReceiptByEmail}
                                    onChange={() => setSendReceiptByEmail(!sendReceiptByEmail)}
                                />
                                <Typography size='small' style={{ whiteSpace: 'nowrap' }}>{sendByEmailLabel}</Typography>
                            </label>
                        </div>
                    </ModalBody>
                }
                <ModalFooter>
                    <AppButton onClick={() => setConfirming(null)}>{t(TK.close)}</AppButton>
                    {confirming !== 'resend' && confirming !== 'receipt' && (
                        <AppButton
                            color={confirming === 'finalize' ? 'success' : 'danger'}
                            onClick={handleActionConfimation}
                        >
                            {t(confirming === 'cancel' ? TK.cancelInvoice : confirming === 'delete' ? TK.deleteDraft : TK.finalize)}
                        </AppButton>
                    )}
                    {confirming === 'receipt' && (
                        <AppButton
                            color='success'
                            onClick={handleActionConfimation}
                            disabled={!receiptPaymentMethod || receiptAmount <= 0}
                        >
                            {t(TK.emitReceipt)}
                        </AppButton>
                    )}
                    {confirming === 'resend' && (
                        <AppButton
                            color='info'
                            onClick={handleActionConfimation}
                        >
                            {t(TK.sendInvoiceToClientEmail)}
                        </AppButton>
                    )}
                </ModalFooter>
            </Modal>

            {/* Payment Validation Modal */}
            <Modal isOpen={showPaymentValidationModal}>
                <ModalHeader toggle={() => setShowPaymentValidationModal(false)}>
                    {paymentValidationData && Math.abs(paymentValidationData.difference) > 0.01
                        ? t(TK.invoiceValueMismatch)
                        : t(TK.invoiceReceiptRequiresPaymentMethod)}
                </ModalHeader>
                <ModalBody>
                    {paymentValidationData && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
                            {Math.abs(paymentValidationData.difference) > 0.01 ? (
                                <>
                                    <Typography size='small'>
                                        {t(TK.invoiceValueMismatch)}. {t(TK.selectPaymentMethodForDifference)}.
                                    </Typography>

                                    <div style={{
                                        padding: 'var(--spacer-S)',
                                        backgroundColor: 'var(--bg-light)',
                                        borderRadius: 'var(--spacer-XXS)',
                                        borderLeft: 'var(--border-thick) solid var(--warning-color)'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--spacer-XS)' }}>
                                            <Typography size='small'><strong>{t(TK.invoiceTotalValue)}:</strong></Typography>
                                            <Typography size='small'>{formatEuros(paymentValidationData.invoiceTotal)}</Typography>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--spacer-XS)' }}>
                                            <Typography size='small'><strong>{t(TK.originalAppointmentPackValue)}:</strong></Typography>
                                            <Typography size='small'>{formatEuros(paymentValidationData.originalTotal)}</Typography>
                                        </div>
                                        <hr style={{ margin: 'var(--spacer-XS) 0' }} />
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography size='small'><strong>{t(TK.valueDifference)}:</strong></Typography>
                                            <Typography size='small' style={{
                                                color: paymentValidationData.difference > 0 ? 'var(--warning-color)' : 'var(--success-color)',
                                                fontWeight: 'bold'
                                            }}>
                                                {paymentValidationData.difference > 0 ? '+' : ''}{formatEuros(paymentValidationData.difference)}
                                            </Typography>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <Typography size='small'>
                                    {t(TK.invoiceReceiptRequiresPaymentMethod)}. {t(TK.selectPaymentMethod)}.
                                </Typography>
                            )}

                            <FormField label={t(TK.paymentType)}>
                                <AutocompleteTextField
                                    value={selectedPaymentMethod}
                                    options={paymentMethods}
                                    onValueChanged={setSelectedPaymentMethod}
                                    placeholder={t(TK.selectPaymentMethod)}
                                />
                            </FormField>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <AppButton onClick={() => setShowPaymentValidationModal(false)}>{t(TK.cancel)}</AppButton>
                    <AppButton
                        color='success'
                        onClick={handlePaymentValidationConfirm}
                        disabled={!selectedPaymentMethod}
                    >
                        {t(TK.continueWithInvoiceEmission)}
                    </AppButton>
                </ModalFooter>
            </Modal>

            <Modal isOpen={registerModalOpen} toggle={() => setRegisterModalOpen(false)}>
                <ModalHeader toggle={() => setRegisterModalOpen(false)}>{t(TK.cashRegisterSelectRegister)}</ModalHeader>
                <ModalBody style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                    <AppButton color="success" onClick={() => handleRegisterSelect('')}>
                        {t(TK.cashRegisterOwnRegister)}
                    </AppButton>
                    {sharedRegisterNames.map(name => (
                        <AppButton key={name} color="success" onClick={() => handleRegisterSelect(name)}>
                            {name}
                        </AppButton>
                    ))}
                </ModalBody>
            </Modal>
        </>
    );
}

export default ClientInvoicesTable;
